const ASSIGNMENT_PREFIX = "midpoint_cv_exp_";

function hashString(input) {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function pickVariant(experiment, visitorId) {
  const variants = experiment?.variants || [];
  if (!variants.length) {
    return null;
  }

  const seed = hashString(`${experiment.key}:${visitorId}`);
  const totalWeight = variants.reduce((sum, variant) => sum + Number(variant.weight || 0), 0);
  if (totalWeight <= 0) {
    return variants[0];
  }

  const bucket = (seed % 10000) / 10000;
  const allocation = Number(experiment.trafficAllocation ?? 1);
  if (bucket > allocation) {
    return null;
  }

  let cursor = 0;
  const normalized = (seed % 10000) / 10000;
  for (const variant of variants) {
    cursor += Number(variant.weight || 0) / totalWeight;
    if (normalized <= cursor) {
      return variant;
    }
  }

  return variants[variants.length - 1];
}

function deepMerge(target, source) {
  if (!source || typeof source !== "object") {
    return target;
  }

  const output = Array.isArray(target) ? [...target] : { ...target };
  Object.entries(source).forEach(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      output[key] = deepMerge(output[key] || {}, value);
    } else {
      output[key] = value;
    }
  });
  return output;
}

function applyPageOverrides(manifest, pageOverrides = {}) {
  const pages = (manifest.pages || []).map((page) => {
    const override = pageOverrides[page.route];
    if (!override) {
      return page;
    }
    return deepMerge(page, override);
  });
  return { ...manifest, pages };
}

export function resolveExperimentAssignments(manifest, visitorId) {
  const experiments = manifest?.experiments || [];
  const assignments = {};

  experiments.forEach((experiment) => {
    if (!experiment?.enabled || !experiment?.key) {
      return;
    }

    const storageKey = `${ASSIGNMENT_PREFIX}${experiment.key}`;
    let variant = null;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        variant = experiment.variants?.find((entry) => entry.key === stored) || null;
      }
    } catch {
      variant = null;
    }

    if (!variant) {
      variant = pickVariant(experiment, visitorId);
      if (variant?.key) {
        try {
          localStorage.setItem(storageKey, variant.key);
        } catch {
          // ignore storage errors
        }
      }
    }

    if (variant) {
      assignments[experiment.key] = variant.key;
    }
  });

  return assignments;
}

export function applyExperimentOverrides(manifest, visitorId) {
  if (!manifest || !Array.isArray(manifest.experiments)) {
    return { manifest, assignments: {} };
  }

  const assignments = resolveExperimentAssignments(manifest, visitorId);
  let nextManifest = { ...manifest };

  manifest.experiments.forEach((experiment) => {
    if (!experiment?.enabled) {
      return;
    }
    const variantKey = assignments[experiment.key];
    const variant = experiment.variants?.find((entry) => entry.key === variantKey);
    if (!variant?.overrides) {
      return;
    }

    if (variant.overrides.pages) {
      nextManifest = applyPageOverrides(nextManifest, variant.overrides.pages);
    }
  });

  return { manifest: nextManifest, assignments };
}
